/**
 * V37.48 — Journal d'envois email (audit RGPD + debug "je n'ai pas recu mon email").
 *
 * Liste paginee + filtres (categorie, destinataire, statut, dates).
 * Endpoint : GET /api/admin/email-audit-log
 */
import { DatePickerFr } from './ui/DatePickerFr';
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle, Inbox, Loader2, Mail, RefreshCw, Search, XCircle, Download, FileSpreadsheet, TrendingUp } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  domium_echeance: { label: 'Rappel échéance DOMIUM', color: 'bg-indigo-100 text-indigo-700' },
  batico_echeance: { label: 'Rappel échéance BATICO', color: 'bg-emerald-100 text-emerald-700' },
  batico_renewal: { label: 'Renouvellement RCDS', color: 'bg-amber-100 text-amber-700' },
  signature: { label: 'Signature / OTP', color: 'bg-purple-100 text-purple-700' },
  proposition: { label: 'Proposition / Devis', color: 'bg-blue-100 text-blue-700' },
  recu: { label: 'Reçu / Facture', color: 'bg-teal-100 text-teal-700' },
  bienvenue: { label: 'Bienvenue', color: 'bg-pink-100 text-pink-700' },
  other: { label: 'Autre', color: 'bg-gray-100 text-gray-600' },
};

export default function EmailAuditLog({ getAuthHeader }) {
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all',
    recipient: '',
    date_debut: '',
    date_fin: '',
  });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ entries: [], total: 0 });
  const [loading, setLoading] = useState(false);
  // V37.49 — Serie temporelle pour le graphique Recharts
  const [series, setSeries] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // V37.49 — Chargement de la serie temporelle
  const fetchSeries = useCallback(async (days = 30) => {
    setSeriesLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-audit-log/timeseries?days=${days}`, { headers: getAuthHeader() });
      setSeries(res.data?.series || []);
    } catch {
      // non-bloquant
      setSeries([]);
    } finally {
      setSeriesLoading(false);
    }
  }, [getAuthHeader]);

  // V37.49 — Init unique de la serie temporelle (pattern React 19, comme EmailNotificationToggles)
  const seriesFetched = React.useRef(null);
  if (seriesFetched.current == null) {
    seriesFetched.current = true;
    fetchSeries(30);
  }

  // V37.49 — Export CSV / XLSX en respectant les filtres
  const handleExport = useCallback(async (fmt) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.category && filters.category !== 'all') params.append('category', filters.category);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.recipient) params.append('recipient', filters.recipient);
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);
      params.append('fmt', fmt);
      const res = await axios.get(`${API}/admin/email-audit-log/export?${params.toString()}`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: fmt === 'csv'
          ? 'text/csv;charset=utf-8'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
      a.download = `email_audit_log_${ts}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Erreur export ${fmt.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  }, [filters, getAuthHeader]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category && filters.category !== 'all') params.append('category', filters.category);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.recipient) params.append('recipient', filters.recipient);
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);
      params.append('page', String(page));
      params.append('limit', '50');
      const res = await axios.get(`${API}/admin/email-audit-log?${params.toString()}`, { headers: getAuthHeader() });
      setData({ entries: res.data?.entries || [], total: res.data?.total || 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement du journal');
    } finally {
      setLoading(false);
    }
  }, [filters, page, getAuthHeader]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / 50));

  return (
    <div className="space-y-4" data-testid="email-audit-log">
      {/* V37.49 — Graphique evolution des envois (30 jours) */}
      <Card className="border-purple-200" data-testid="audit-evolution-chart">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-200 py-3">
          <CardTitle className="flex items-center gap-2 text-purple-900 text-base">
            <TrendingUp className="h-5 w-5" />
            Évolution des envois (30 derniers jours)
          </CardTitle>
        </CardHeader>
        <CardContent className="py-4">
          {seriesLoading ? (
            <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-purple-500" /></div>
          ) : series.length === 0 ? (
            <p className="text-sm text-gray-400 italic text-center py-6">Aucun envoi sur les 30 derniers jours.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={series} margin={{ top: 5, right: 20, bottom: 5, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <RTooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  labelStyle={{ fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="sent" stroke="#10b981" name="Envoyés" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="failed" stroke="#ef4444" name="Échecs" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="total" stroke="#6366f1" name="Total" strokeWidth={1} strokeDasharray="4 4" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card className="border-blue-200">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-200">
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Inbox className="h-5 w-5" />
            Journal d&apos;envois email
          </CardTitle>
          <CardDescription className="text-blue-700">
            Tous les emails effectivement envoyes (transactionnels, signature, rappels, etc.). Filtrez pour audit RGPD ou debug.
          </CardDescription>
        </CardHeader>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-1">
              <Label className="text-xs text-gray-500">Catégorie</Label>
              <Select value={filters.category} onValueChange={(v) => setFilters((p) => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="audit-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs text-gray-500">Statut</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="audit-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="sent">Envoyé</SelectItem>
                  <SelectItem value="failed">Échec</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-gray-500">Destinataire</Label>
              <Input
                placeholder="email partiel..."
                value={filters.recipient}
                onChange={(e) => setFilters((p) => ({ ...p, recipient: e.target.value }))}
                data-testid="audit-filter-recipient"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Date début</Label>
              <DatePickerFr
                value={filters.date_debut}
                onChange={(v) => setFilters((p) => ({ ...p, date_debut: v }))}
                className="w-full"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Date fin</Label>
              <DatePickerFr
                value={filters.date_fin}
                onChange={(v) => setFilters((p) => ({ ...p, date_fin: v }))}
                className="w-full"
              />
            </div>
          </div>
          <div className="mt-3 flex gap-2 items-center">
            <Button onClick={() => { setPage(1); fetchLogs(); }} disabled={loading} data-testid="audit-search">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
              Charger
            </Button>
            <Button
              variant="outline"
              onClick={() => { setFilters({ category: 'all', status: 'all', recipient: '', date_debut: '', date_fin: '' }); setPage(1); }}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {/* V37.49 — Exports CSV / XLSX (respectent les filtres) */}
            <Button
              variant="outline"
              onClick={() => handleExport('csv')}
              disabled={exporting}
              data-testid="audit-export-csv"
              title="Exporter en CSV avec les filtres actuels"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
              CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('xlsx')}
              disabled={exporting}
              data-testid="audit-export-xlsx"
              title="Exporter en XLSX avec les filtres actuels"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
              XLSX
            </Button>
            <span className="text-xs text-gray-500 ml-auto">{data.total} entrée{data.total > 1 ? 's' : ''}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" /></div>
          ) : data.entries.length === 0 ? (
            <div className="py-12 text-center">
              <Mail className="h-12 w-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-700 font-medium">Aucun email trouvé</p>
              <p className="text-xs text-gray-400 mt-2">
                Cliquez sur <strong>Charger</strong> pour afficher les envois enregistrés.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-semibold text-gray-700">Date</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Destinataire</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Sujet</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Catégorie</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Statut</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Livraison Brevo</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Brevo ID</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e, idx) => {
                    const cat = CATEGORY_LABELS[e.category] || CATEGORY_LABELS.other;
                    return (
                      <tr key={e.id || idx} className="border-b hover:bg-blue-50/50" data-testid={`audit-row-${idx}`}>
                        <td className="p-3 text-xs font-mono text-gray-600 whitespace-nowrap">
                          {e.timestamp ? new Date(e.timestamp).toLocaleString('fr-FR') : '-'}
                        </td>
                        <td className="p-3 text-xs text-gray-800">{e.recipient || '-'}</td>
                        <td className="p-3 text-sm text-gray-900 max-w-md truncate" title={e.subject}>{e.subject || '-'}</td>
                        <td className="p-3">
                          <Badge className={`text-[10px] ${cat.color} border`}>{cat.label}</Badge>
                        </td>
                        <td className="p-3">
                          {e.status === 'sent' ? (
                            <Badge className="text-[10px] bg-emerald-700 text-white border-emerald-900 gap-1 font-semibold">
                              <CheckCircle className="h-3 w-3" /> Envoyé
                            </Badge>
                          ) : (
                            <Badge className="text-[10px] bg-red-700 text-white border-red-900 gap-1 font-semibold" title={e.error || ''}>
                              <XCircle className="h-3 w-3" /> Échec
                            </Badge>
                          )}
                        </td>
                        <td className="p-3 space-x-1 whitespace-nowrap" data-testid={`audit-delivery-${idx}`}>
                          {e.delivered === true && (
                            <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold">Livré</Badge>
                          )}
                          {e.delivered === false && (
                            <Badge className="text-[10px] bg-red-50 text-red-700 border-red-200 font-semibold" title={e.bounce_reason || ''}>Rebond</Badge>
                          )}
                          {e.opened_at && (
                            <Badge className="text-[10px] bg-blue-50 text-blue-700 border-blue-200 font-semibold">Ouvert</Badge>
                          )}
                          {e.clicked_at && (
                            <Badge className="text-[10px] bg-purple-50 text-purple-700 border-purple-200 font-semibold">Cliqué</Badge>
                          )}
                          {!e.delivered && !e.opened_at && !e.clicked_at && e.delivered !== false && (
                            <span className="text-[10px] text-gray-400 italic">en attente…</span>
                          )}
                        </td>
                        <td className="p-3 text-[10px] text-gray-400 font-mono">{e.brevo_message_id || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {data.total > 50 && (
        <div className="flex justify-center items-center gap-2 text-sm">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(p => p - 1); setTimeout(fetchLogs, 0); }}>
            Précédent
          </Button>
          <span className="text-gray-600">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => { setPage(p => p + 1); setTimeout(fetchLogs, 0); }}>
            Suivant
          </Button>
        </div>
      )}
    </div>
  );
}
